"use client";

import { setFieldError, setFieldValue, setLoading } from "@/stores";
import { useForm, usePasswordValidation } from "@/hooks";
import { axiosInstance } from "@/helpers";
import { confirmPasswordReset } from "@vetty/api-sdk";

import { useTranslations } from "@/i18n";
import { Button } from "@vetty/ui";

/**
 * ResetPasswordSubmitButton Component
 *
 * Submit button for password reset form with validation and API integration.
 * Handles password validation, confirmation matching, and reset API calls.
 */
export const ResetPasswordSubmitButton = () => {
  const t = useTranslations("resetPassword.form");
  const tErrors = useTranslations("resetPassword.errors");

  const { values, loading } = useForm();
  const { isPasswordValid } = usePasswordValidation(
    String(values.password ?? "")
  );

  /**
   * Validates password confirmation
   * @returns validation result with error flags
   */
  const validatePasswordConfirmation = () => {
    const password = String(values.password ?? "");
    const confirmPassword = String(values.confirmPassword ?? "");

    const emptyConfirmPasswordError = confirmPassword.length === 0;
    const passwordMismatchError = password !== confirmPassword;

    return {
      emptyConfirmPasswordError,
      passwordMismatchError,
      isValid: !emptyConfirmPasswordError && !passwordMismatchError,
    };
  };

  /**
   * Handles successful password reset
   * Sets form status to success state
   */
  const handleSuccess = () => {
    setFieldValue("status", true);
  };

  /**
   * Handles password reset errors
   * Sets form status to error and shows error message
   */
  const handleError = () => {
    setFieldValue("status", false);
    setFieldError("email", tErrors("somethingWentWrong"));
  };

  /**
   * Submits password reset form with validation and error handling
   */
  const handleFormSubmit = async () => {
    setLoading(true);

    // Validate password requirements
    const passwordValidation = { isValid: isPasswordValid };

    // Validate password confirmation
    const confirmationValidation = validatePasswordConfirmation();

    // Set validation errors
    setFieldError(
      "password",
      !passwordValidation.isValid ? tErrors("passwordRequirements") : ""
    );
    setFieldError(
      "confirmPassword",
      confirmationValidation.emptyConfirmPasswordError
        ? tErrors("passwordsDoNotMatch")
        : confirmationValidation.passwordMismatchError
        ? tErrors("passwordsDoNotMatch")
        : ""
    );

    // Stop if validation fails
    if (!passwordValidation.isValid || !confirmationValidation.isValid) {
      setLoading(false);
      return;
    }

    try {
      // Send password reset request
      const { data } = await confirmPasswordReset(axiosInstance, {
        token: String(values.token ?? ""),
        newPassword: String(values.password ?? ""),
        confirmPassword: String(values.confirmPassword ?? ""),
      });

      console.log(data);
      handleSuccess();
    } catch (error) {
      console.error("Password reset error:", error);
      handleError();
    }
  };

  return (
    <Button disabled={loading} loading={loading} onClick={handleFormSubmit}>
      {t("reset")}
    </Button>
  );
};
